import React, { useState, useEffect, useCallback, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Workflow, ShoppingCart, PackageCheck, Layers, ClipboardList, Scissors,
  Settings2, Users, Send, FileText, Inbox, Boxes, Shirt, Puzzle, Receipt, FileCheck2,
  BarChart3, ShieldCheck, Search, LogOut, ChevronDown, Warehouse, Cog, UserCog, Menu as MenuIcon,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const NAV = [
  { section: "Overview", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  ]},
  { section: "Production", items: [
    { to: "/production-orders", label: "Production Orders", icon: ClipboardList },
    { to: "/cutting", label: "Cutting", icon: Scissors },
    { to: "/challans", label: "Jobber Challans", icon: FileText },
    { to: "/wip", label: "WIP Stock", icon: Boxes },
    { to: "/cleared-slips", label: "Cleared Lots", icon: FileCheck2 },
  ]},
  { section: "Inventory", items: [
    { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
    { to: "/accessories", label: "Accessories", icon: Puzzle },
  ]},
  { section: "Masters", items: [
    { to: "/suppliers", label: "Suppliers", icon: UserCog },
    { to: "/jobbers", label: "Jobbers", icon: Users },
    { to: "/process-master", label: "Process Master", icon: Cog },
    { to: "/jobber-bills", label: "Jobber Bills", icon: Receipt },
  ]},
  { section: "Insights", items: [
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/audit", label: "Audit Trail", icon: ShieldCheck },
    { to: "/settings", label: "Settings", icon: Settings2 },
  ]},
];

const ROLE_PAGES = {
  Purchase: ["/dashboard", "/stock-in-hand", "/purchase-orders", "/fabric-stock", "/accessories", "/suppliers", "/reports"],
  Store: ["/dashboard", "/stock-in-hand", "/purchase-orders", "/fabric-stock", "/accessories", "/cutting", "/wip", "/cleared-slips", "/reports"],
  Production: ["/dashboard", "/stock-in-hand", "/production-orders", "/production-stage", "/cutting", "/process-issue", "/challans", "/receiving", "/wip", "/cleared-slips", "/finished-goods", "/jobbers", "/process-master", "/reports"],
  Accounts: ["/dashboard", "/stock-in-hand", "/sales-orders", "/jobber-bills", "/challans", "/customers", "/finished-goods", "/cleared-slips", "/reports"],
};
const ADMIN_ONLY_PAGES = ["/settings"];
function canSee(role, to) {
  if (role === "Admin") return true;
  if (ADMIN_ONLY_PAGES.includes(to)) return false;
  const pages = ROLE_PAGES[role];
  if (!pages) return true;
  return pages.includes(to);
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef();

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); ref.current?.focus(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(res.data.results || []);
        setOpen(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (r) => {
    setOpen(false); setQ("");
    if (r.kind === "production") navigate(`/production-orders/${r.id}`);
    else if (r.kind === "cutlot") navigate(r.id ? `/production-orders/${r.id}` : "/wip");
    else if (r.kind === "challan") navigate(`/challans/${r.value}`);
    else if (r.kind === "lot") navigate("/wip");
    else if (r.kind === "po") navigate("/purchase-orders");
    else if (r.kind === "sales") navigate("/sales-orders");
    else if (r.kind === "jobber") navigate("/jobbers");
    else if (r.kind === "supplier") navigate("/suppliers");
  };

  return (
    <div className="relative w-full max-w-md">
      <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
      <input
        ref={ref}
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search PO, design, challan, lot, jobber…  (⌘K)"
        className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {results.map((r, i) => (
            <button
              key={i}
              onMouseDown={() => go(r)}
              data-testid={`global-search-result-${i}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between border-b border-slate-50 last:border-0"
            >
              {r.kind === "cutlot" ? (
                <span className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="font-semibold text-slate-800">{r.design_number}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-slate-600">Sheet {r.cutting_sheet_number}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-blue-700 font-medium">Qty {r.qty}</span>
                </span>
              ) : (
                <span className="text-sm text-slate-700 font-medium">{r.value}</span>
              )}
              <span className="text-xs text-slate-400 shrink-0 ml-2">{r.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-slate-50">
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-slate-900/50 z-30 lg:hidden" data-testid="sidebar-overlay" />}
      <aside className={`w-64 bg-slate-900 text-slate-300 flex flex-col shrink-0 fixed inset-y-0 left-0 z-40 transition-transform duration-200 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-800">
          <div className="h-9 w-9 rounded-lg bg-blue-700 text-white grid place-items-center font-head font-extrabold">A</div>
          <div>
            <p className="font-head font-extrabold text-white leading-none">ASV Apparels</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Production ERP</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2.5">
          {NAV.map((grp) => {
            const items = grp.items.filter((it) => canSee(user?.role, it.to));
            if (!items.length) return null;
            return (
            <div key={grp.section} className="mb-3">
              <p className="px-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{grp.section}</p>
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm mb-0.5 transition-colors ${
                      isActive ? "bg-blue-800 text-white font-semibold shadow-sm" : "hover:bg-slate-800 hover:text-white"
                    }`
                  }
                >
                  <it.icon size={16} />
                  {it.label}
                </NavLink>
              ))}
            </div>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 ml-0 lg:ml-64 flex flex-col min-h-screen min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 gap-3 sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900 shrink-0" data-testid="sidebar-toggle" aria-label="Open menu"><MenuIcon size={22} /></button>
          <GlobalSearch />
          <div className="relative">
            <button
              data-testid="user-menu-btn"
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-100"
            >
              {user?.picture ? (
                <img src={user.picture} className="h-8 w-8 rounded-full" alt="" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-blue-700 text-white grid place-items-center text-sm font-semibold">
                  {(user?.name || "U")[0]}
                </div>
              )}
              <div className="text-left hidden sm:block">
                <p className="text-sm font-medium text-slate-800 leading-none">{user?.name}</p>
                <p className="text-[11px] text-blue-700 font-medium">{user?.role}</p>
              </div>
              <ChevronDown size={15} className="text-slate-400" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="text-sm font-medium text-slate-800">{user?.name}</p>
                  <p className="text-xs text-slate-400">{user?.email}</p>
                </div>
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <LogOut size={15} /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 max-w-[1700px] w-full mx-auto min-w-0">{children}</main>
      </div>
    </div>
  );
}
